

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Stage, Layer, Transformer, Rect, Text, Line, Circle, Group } from "react-konva";
import { useTheme } from "@mui/material";

import SeatingChartSeat, { SEAT_RADIUS } from "./SeatingChartSeat";
import SeatingChartSectionShape from "./SeatingChartSectionShape";
import SeatingChartImage from "./SeatingChartImage";
import { createTextareaForText } from "src/utils/canvasTextEditingUtils";
import { getNormalizedRect } from "src/utils/canvasGeometryUtils";
import { handleWheel } from "src/utils/canvasHandlers";
import {
    handleSelectMouseDown,
    handleSelectMouseMove,
    handleSelectMouseUp,
    handleToolHoverMove,
    handleSeatToolClick,
    handleRowMouseDown,
    handleRowMouseMove,
    handleRowMouseUp,
    handleGridMouseDown,
    handleGridMouseMove,
    handleGridMouseUp,
    handleDrawRectMouseDown,
    handleDrawRectMouseMove,
    handleDrawRectMouseUp,
    handleTextToolClick,
    handleSectionMouseDown,
    handleSectionMouseMove,
    handleSectionMouseUp,
    attachTransformerToIds,
} from "src/utils/canvasSeatHandlers";
import { previewRow, previewGrid, guideOrientationForAngle, snapPointToSeats } from "src/utils/canvasSeatGeometryUtils";
import { SEAT_TOOLS } from "src/hooks/useSeatingChartState";

export const CANVAS_ELEMENT_Z_ORDER = { image: 0, rect: 1, text: 2 };

const SeatingChartCanvasStage = ({ state, stageSize }) => {
    const theme = useTheme();
    const {
        sections,
        categories,
        visibleSeats,
        visibleCanvasElements,
        setVisibleCanvasElements,
        focusedSectionId,
        focusSection,
        tool,
        setTool,
        selectedIds,
        setSelectedIds,
        selectedCategoryForNewSeats,
        seatToolsUsable,
        updateSeatPosition,
        updateSeatTransform,
        updateElementPosition,
        moveSectionBy,
        updateSectionTransform,
        updateElementTransform,
        marquee,
        setMarquee,
        ghostPosition,
        setGhostPosition,
        hoverGuide,
        setHoverGuide,
        rowDraft,
        setRowDraft,
        gridDraft,
        setGridDraft,
        sectionDraft,
        setSectionDraft,
        drawingRect,
        setDrawingRect,
        seatSpacing,
        rowSpacing,
        addSeat,
        finalizeRowGroup,
        finalizeGridGroup,
        finalizeNewSection,
        shapeRefs,
        transformerRef,
        stageRef,
        layerRef,
        isSectioned
    } = state;

    const sectionDragSeatsRef = useRef([]);

    const nextRowLabel = useMemo(() => {
        const relevantSeats = isSectioned ? visibleSeats.filter((s) => s.sectionId === focusedSectionId) : visibleSeats;
        const numericLabels = relevantSeats
            .filter((s) => s.metadata?.rowGroup)
            .map((s) => s.rowLabel)
            .filter((label) => /^\d+$/.test(label))
            .map(Number);
        const maxLabel = numericLabels.length ? Math.max(...numericLabels) : 0;
        return String(maxLabel + 1);
    }, [visibleSeats, isSectioned, focusedSectionId]);

    const categoryColorFor = (categoryId) => categories.find((c) => c.id === categoryId)?.color;
    const textEditAdapter = {
        selectedArea: { id: "canvas" },
        setFloorPlans: (updater) =>
            setVisibleCanvasElements((prevArray) => {
                const nextKeyed = typeof updater === "function" ? updater({ canvas: prevArray }) : updater;
                return nextKeyed.canvas ?? prevArray;
            }),
    };

    // ---------------------------------------------------------------------------
    // Middle-mouse pan — same pattern as the floor plan builder's CanvasStage.js
    // ---------------------------------------------------------------------------
    const [isPanning, setIsPanning] = useState(false);
    const lastPanPos = useRef({ x: 0, y: 0 });

    useEffect(() => {
        const stage = stageRef.current;
        if (!stage) return;
        const container = stage.container();

        const handleContainerMouseDown = (e) => {
            if (e.button === 1 && !document.querySelector("textarea")) {
                e.preventDefault();
                setIsPanning(true);
                lastPanPos.current = { x: e.clientX, y: e.clientY };
            }
        };
        const handleWindowMouseMove = (e) => {
            if (!isPanning) return;
            const dx = e.clientX - lastPanPos.current.x;
            const dy = e.clientY - lastPanPos.current.y;
            const pos = stage.position();
            stage.position({ x: pos.x + dx, y: pos.y + dy });
            stage.batchDraw();
            lastPanPos.current = { x: e.clientX, y: e.clientY };
        };
        const handleWindowMouseUp = (e) => {
            if (e.button === 1) setIsPanning(false);
        };

        container.addEventListener("mousedown", handleContainerMouseDown);
        window.addEventListener("mousemove", handleWindowMouseMove);
        window.addEventListener("mouseup", handleWindowMouseUp);
        return () => {
            container.removeEventListener("mousedown", handleContainerMouseDown);
            window.removeEventListener("mousemove", handleWindowMouseMove);
            window.removeEventListener("mouseup", handleWindowMouseUp);
        };
    }, [isPanning, stageRef]);

    // ---------------------------------------------------------------------------
    // Mouse pipeline
    // ---------------------------------------------------------------------------
    const onMouseDown = (e) => {
        if (tool === SEAT_TOOLS.SELECT) handleSelectMouseDown(e, stageRef, setMarquee);
        else if (tool === SEAT_TOOLS.SECTION) handleSectionMouseDown(e, stageRef, setSectionDraft);
        else if (tool === SEAT_TOOLS.ROW) handleRowMouseDown(e, stageRef, setRowDraft, visibleSeats);
        else if (tool === SEAT_TOOLS.GRID) handleGridMouseDown(e, stageRef, setGridDraft, visibleSeats);
        else if (tool === SEAT_TOOLS.DRAW) handleDrawRectMouseDown(e, stageRef, setDrawingRect);
    };

    const onMouseMove = (e) => {
        if (tool === SEAT_TOOLS.SELECT) {
            handleSelectMouseMove(e, stageRef, marquee, setMarquee);
        } else if (tool === SEAT_TOOLS.SEAT) {
            handleToolHoverMove(e, stageRef, visibleSeats, setGhostPosition, setHoverGuide);
        } else if (tool === SEAT_TOOLS.ROW) {
            if (rowDraft) handleRowMouseMove(e, stageRef, rowDraft, setRowDraft, visibleSeats);
            else handleToolHoverMove(e, stageRef, visibleSeats, setGhostPosition, setHoverGuide);
        } else if (tool === SEAT_TOOLS.GRID) {
            if (gridDraft) handleGridMouseMove(e, stageRef, gridDraft, setGridDraft, visibleSeats);
            else handleToolHoverMove(e, stageRef, visibleSeats, setGhostPosition, setHoverGuide);
        } else if (tool === SEAT_TOOLS.SECTION) {
            handleSectionMouseMove(e, stageRef, sectionDraft, setSectionDraft);
        } else if (tool === SEAT_TOOLS.DRAW) {
            handleDrawRectMouseMove(e, stageRef, drawingRect, setDrawingRect);
        }
    };

    const onMouseUp = () => {
        if (tool === SEAT_TOOLS.SELECT) {
            handleSelectMouseUp(marquee, setMarquee, visibleSeats, shapeRefs, setSelectedIds, transformerRef);
        } else if (tool === SEAT_TOOLS.ROW) {
            handleRowMouseUp(rowDraft, setRowDraft, seatSpacing, focusedSectionId, selectedCategoryForNewSeats, finalizeRowGroup, nextRowLabel);
        } else if (tool === SEAT_TOOLS.GRID) {
            handleGridMouseUp(gridDraft, setGridDraft, seatSpacing, rowSpacing, focusedSectionId, selectedCategoryForNewSeats, finalizeGridGroup, nextRowLabel);
        } else if (tool === SEAT_TOOLS.SECTION) {
            handleSectionMouseUp(sectionDraft, setSectionDraft, finalizeNewSection);
        } else if (tool === SEAT_TOOLS.DRAW) {
            const hadRect = drawingRect && Math.abs(drawingRect.width) >= 8 && Math.abs(drawingRect.height) >= 8;
            handleDrawRectMouseUp(drawingRect, setDrawingRect, (rect) => setVisibleCanvasElements((prev) => [...prev, rect]));
            if (hadRect) setTool(SEAT_TOOLS.SELECT);
        }
    };

    const onStageClick = (e) => {
        if (tool === SEAT_TOOLS.SEAT && seatToolsUsable) {
            handleSeatToolClick(e, stageRef, focusedSectionId, selectedCategoryForNewSeats, addSeat, visibleSeats);
        } else if (tool === SEAT_TOOLS.TEXT) {
            handleTextToolClick(e, stageRef, (text) => setVisibleCanvasElements((prev) => [...prev, text]));
            setTool(SEAT_TOOLS.SELECT);
        } else if (tool === SEAT_TOOLS.SELECT && e.target === stageRef.current) {
            setSelectedIds([]);
            if (transformerRef.current) transformerRef.current.nodes([]);
        }
    };

    // ---------------------------------------------------------------------------
    // Selection: a single click on a grouped seat selects the WHOLE group.
    // Double-click drills into just that one seat.
    // ---------------------------------------------------------------------------
    const attachTransformerTo = (ids) => attachTransformerToIds(ids, visibleSeats, shapeRefs, transformerRef);

    const handleSelectShape = (e, id) => {
        if (tool !== SEAT_TOOLS.SELECT) return;
        const isMultiSelect = e?.evt?.ctrlKey || e?.evt?.metaKey;

        const seat = visibleSeats.find((s) => s.id === id);
        const rowGroupId = seat?.metadata?.rowGroup?.rowGroupId;

        let next;
        if (!isMultiSelect && rowGroupId) {
            // plain click on a grouped seat -> select every seat in that group
            next = visibleSeats.filter((s) => s.metadata?.rowGroup?.rowGroupId === rowGroupId).map((s) => s.id);
        } else {
            next = isMultiSelect
                ? selectedIds.includes(id)
                    ? selectedIds.filter((x) => x !== id)
                    : [...selectedIds, id]
                : [id];
        }
        setSelectedIds(next);
        attachTransformerTo(next);
    };

    const handleSeatDoubleClick = (e, id) => {
        if (tool !== SEAT_TOOLS.SELECT) return;
        // drill into exactly this one seat, isolated from its group — still
        // undraggable (see seatDraggable below), just selectable for
        // delete/category assignment
        setSelectedIds([id]);
        attachTransformerTo([id]);
    };

    const selectionKind = (() => {
        if (!selectedIds.length) return "none";
        const allSeats = selectedIds.every((id) => visibleSeats.some((s) => s.id === id));
        return allSeats ? "seats" : "other";
    })();

    const isCompleteRowSelection = (() => {
        if (selectionKind !== "seats" || !selectedIds.length) return false;
        const rowGroupIds = new Set(
            selectedIds.map((id) => visibleSeats.find((s) => s.id === id)?.metadata?.rowGroup?.rowGroupId).filter(Boolean),
        );
        if (!rowGroupIds.size) return false; // no grouped seats at all — nothing to rigidly move as "a row"
        for (const gid of rowGroupIds) {
            const groupSeatIds = visibleSeats.filter((s) => s.metadata?.rowGroup?.rowGroupId === gid).map((s) => s.id);
            if (!groupSeatIds.every((id) => selectedIds.includes(id))) return false;
        }
        // no stray ungrouped seat mixed into an otherwise-complete row selection
        return selectedIds.every((id) => rowGroupIds.has(visibleSeats.find((s) => s.id === id)?.metadata?.rowGroup?.rowGroupId));
    })();

    // a seat is draggable only if it does NOT belong to a row/grid group —
    // grouped seats can only move as a whole group, via the Transformer drag
    // below, regardless of whether 1 (drilled-in) or many are selected
    const seatDraggable = (seat) => tool === SEAT_TOOLS.SELECT && !seat.metadata?.rowGroup;

    // ---------------------------------------------------------------------------
    // Live previews
    // ---------------------------------------------------------------------------
    const rowPreview = rowDraft
        ? previewRow(rowDraft.start, rowDraft.current, { seatSpacing, shift: rowDraft.shift, alt: rowDraft.alt, startRowLabel: nextRowLabel })
        : null;
    const gridPreview = gridDraft ? previewGrid(gridDraft.start, gridDraft.current, { seatSpacing, rowSpacing, startRowLabel: nextRowLabel }) : null;
    const guideOrientation = rowPreview ? guideOrientationForAngle(rowPreview.snappedAngle) : null;

    const showPreClickGhost =
        (tool === SEAT_TOOLS.SEAT || (tool === SEAT_TOOLS.ROW && !rowDraft) || (tool === SEAT_TOOLS.GRID && !gridDraft)) &&
        seatToolsUsable &&
        ghostPosition;

    // ---------------------------------------------------------------------------
    // Snap-while-moving an existing selected group, via the Transformer's own
    // native drag (only active when 2+ nodes are attached — see draggable prop
    // on <Transformer /> below).
    // ---------------------------------------------------------------------------
    const [groupDragGuide, setGroupDragGuide] = useState({ guideX: null, guideY: null });

    const handleTransformerDragMove = (e) => {
        if (selectedIds.length < 2) return;
        // snap using the first selected seat as the representative point, against
        // every seat NOT in the current selection
        const otherSeats = visibleSeats.filter((s) => !selectedIds.includes(s.id));
        const firstId = selectedIds[0];
        const firstNode = shapeRefs.current[firstId];
        if (!firstNode) return;

        const raw = { x: firstNode.x(), y: firstNode.y() };
        const { point, guideX, guideY } = snapPointToSeats(raw, otherSeats);
        setGroupDragGuide({ guideX, guideY });

        const dx = point.x - raw.x;
        const dy = point.y - raw.y;
        if (dx !== 0 || dy !== 0) {
            // nudge the whole transformer (and therefore every attached node) by
            // the snap correction
            e.target.x(e.target.x() + dx);
            e.target.y(e.target.y() + dy);
        }
    };

    const handleTransformerDragEnd = () => {
        setGroupDragGuide({ guideX: null, guideY: null });
        selectedIds.forEach((id) => {
            const node = shapeRefs.current[id];
            if (node) updateSeatPosition(id, { x: node.x(), y: node.y() });
        });
    };

    const SEAT_HIT_RADIUS = 20; // a bit more forgiving than the seat's own 14px radius

    const handleTransformerDblClick = () => {
        // only relevant for grouped multi-seat selections — a lone drilled-in
        // seat isn't overdrawn, so its own onDblClick still fires normally
        if (!isCompleteRowSelection) return;
        if (selectionKind !== "seats" || selectedIds.length < 2) return;

        const stage = stageRef.current;
        const pointer = stage.getPointerPosition();
        if (!pointer) return;
        const point = stage.getAbsoluteTransform().copy().invert().point(pointer);

        let closest = null;
        let closestDist = Infinity;
        for (const id of selectedIds) {
            const seat = visibleSeats.find((s) => s.id === id);
            if (!seat) continue;
            const d = Math.hypot(seat.x - point.x, seat.y - point.y);
            if (d < closestDist) {
                closestDist = d;
                closest = seat;
            }
        }

        if (closest && closestDist <= SEAT_HIT_RADIUS) {
            setSelectedIds([closest.id]);
            attachTransformerTo([closest.id]);
        }
    };


    const orderedCanvasElements = useMemo(
        () =>
            [...visibleCanvasElements].sort(
                (a, b) => (CANVAS_ELEMENT_Z_ORDER[a.type] ?? 0) - (CANVAS_ELEMENT_Z_ORDER[b.type] ?? 0),
            ),
        [visibleCanvasElements],
    );

    const ROW_LABEL_WIDTH = 24;
    const ROW_LABEL_HEIGHT = 16;

    const rowGroupLabels = useMemo(() => {
        const seen = new Map();
        for (const seat of visibleSeats) {
            const rg = seat.metadata?.rowGroup;
            if (!rg || seen.has(rg.rowGroupId)) continue;

            const groupSeats = visibleSeats.filter((s) => s.metadata?.rowGroup?.rowGroupId === rg.rowGroupId);
            if (!groupSeats.length) continue;

            const col = (s) => Number(s.metadata.positionKey.split("-")[1]);
            const first = groupSeats.reduce((a, b) => (col(a) < col(b) ? a : b)); // physical col 0
            const last = groupSeats.reduce((a, b) => (col(a) > col(b) ? a : b));  // physical col N-1

            // derive the row's CURRENT angle from the seats' live positions,
            // not rg.angle — a plain drag/rotate never updates metadata.rowGroup,
            // so rg.angle can be stale; this keeps the label glued to reality
            const rawDx = last.x - first.x;
            const rawDy = last.y - first.y;
            const len = Math.hypot(rawDx, rawDy) || 1;
            const dir = { x: rawDx / len, y: rawDy / len };

            // still pick whichever physical end is actually left/top-most on
            // screen, so a row drawn right-to-left (or bottom-up) still gets its
            // label on the left/top rather than wherever "seat 0" landed
            const isHorizontalish = Math.abs(dir.x) >= Math.abs(dir.y);
            const endpoint = isHorizontalish ? (first.x <= last.x ? first : last) : (first.y <= last.y ? first : last);

            // one seat-spacing further out, continuing the row's own line — as
            // if the label sat at "seat -1". If the left/top endpoint is
            // physical col 0, that's the -dir direction (seats increase in
            // +dir from there); if it's the last seat instead, continuing
            // outward from it is +dir.
            const outwardDir = endpoint === first ? { x: -dir.x, y: -dir.y } : dir;
            const rotationDeg = (Math.atan2(dir.y, dir.x) * 180) / Math.PI;

            seen.set(rg.rowGroupId, {
                id: rg.rowGroupId,
                x: endpoint.x + outwardDir.x * rg.seatSpacing,
                y: endpoint.y + outwardDir.y * rg.seatSpacing,
                rotation: rotationDeg,
                text: rg.startRowLabel,
            });
        }
        return Array.from(seen.values());
    }, [visibleSeats]);

    return (
        <Stage
            width={stageSize.width}
            height={stageSize.height}
            ref={stageRef}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onClick={onStageClick}
            onWheel={(e) => handleWheel(e, stageRef)}
            style={{ cursor: tool === SEAT_TOOLS.SEAT ? "none" : "default" }}
        >
            <Layer ref={layerRef}>
                {/* decorative elements */}
                {orderedCanvasElements.map((shape) => {
                    if (shape.type === "rect") {
                        return (
                            <Rect
                                key={shape.id}
                                {...getNormalizedRect(shape)}
                                fill="#3a3a3a"
                                cornerRadius={8}
                                draggable={tool === SEAT_TOOLS.SELECT}
                                rotation={shape.rotation}
                                onClick={(e) => handleSelectShape(e, shape.id)}
                                ref={(node) => (shapeRefs.current[shape.id] = node)}
                                onDragEnd={(e) => updateElementPosition(shape.id, { x: e.target.x(), y: e.target.y() })}
                                onTransformEnd={(e) => {
                                    const node = e.target;
                                    const width = Math.max(8, node.width() * node.scaleX());
                                    const height = Math.max(8, node.height() * node.scaleY());
                                    node.scaleX(1);
                                    node.scaleY(1);
                                    updateElementTransform(shape.id, { x: node.x(), y: node.y(), width, height, rotation: node.rotation() });
                                }}
                            />
                        );
                    }
                    if (shape.type === "text") {
                        return (
                            <Text
                                key={shape.id}
                                {...shape}
                                fill="white"
                                draggable={tool === SEAT_TOOLS.SELECT}
                                onClick={(e) => handleSelectShape(e, shape.id)}
                                onDblClick={() =>
                                    createTextareaForText(shape, shapeRefs, transformerRef, stageRef, textEditAdapter.setFloorPlans, textEditAdapter.selectedArea)
                                }
                                ref={(node) => (shapeRefs.current[shape.id] = node)}
                                onDragEnd={(e) => updateElementPosition(shape.id, { x: e.target.x(), y: e.target.y() })}
                                onTransformEnd={(e) => {
                                    const node = e.target;
                                    const width = Math.max(40, node.width() * node.scaleX());
                                    node.scaleX(1);
                                    node.scaleY(1);
                                    updateElementTransform(shape.id, { x: node.x(), y: node.y(), width, rotation: node.rotation() });
                                }}
                            />
                        );
                    }
                    if (shape.type === "image") {
                        return (
                            <SeatingChartImage
                                key={shape.id}
                                shape={shape}
                                draggable={tool === SEAT_TOOLS.SELECT}
                                shapeRefs={shapeRefs}
                                onSelect={handleSelectShape}
                                onDragEnd={(e, id) => updateElementPosition(id, { x: e.target.x(), y: e.target.y() })}
                                onTransformEnd={(e, id) => {
                                    const node = e.target;
                                    const width = Math.max(20, node.width() * node.scaleX());
                                    const height = Math.max(20, node.height() * node.scaleY());
                                    node.scaleX(1);
                                    node.scaleY(1);
                                    updateElementTransform(id, { x: node.x(), y: node.y(), width, height, rotation: node.rotation() });
                                }}
                            />
                        );
                    }
                    return null;
                })}

                {sections.map((section) => (
                    <SeatingChartSectionShape
                        key={section.id}
                        section={section}
                        isSelected={selectedIds.includes(section.id)}
                        isFocused={focusedSectionId === section.id}
                        draggable={tool === SEAT_TOOLS.SELECT && selectedIds.includes(section.id)}
                        shapeRefs={shapeRefs}
                        onSelect={(e) => handleSelectShape(e, section.id)}
                        onOpen={() => focusSection(section)}
                        onDragStart={() => {
                            sectionDragSeatsRef.current = visibleSeats
                                .filter((s) => s.sectionId === section.id)
                                .map((s) => ({ id: s.id, x: s.x, y: s.y }));
                        }}
                        onDragMove={(e) => {
                            const dx = e.target.x() - section.x;
                            const dy = e.target.y() - section.y;
                            for (const seat of sectionDragSeatsRef.current) {
                                const node = shapeRefs.current[seat.id];
                                if (node) node.position({ x: seat.x + dx, y: seat.y + dy });
                            }
                            e.target.getLayer()?.batchDraw();
                        }}
                        onDragEnd={(dx, dy, id) => {
                            sectionDragSeatsRef.current = [];
                            moveSectionBy(id, dx, dy);
                        }}
                        onTransformEnd={(e, id) => {
                            const node = e.target;
                            const width = Math.max(40, node.width() * node.scaleX());
                            const height = Math.max(40, node.height() * node.scaleY());
                            node.scaleX(1);
                            node.scaleY(1);
                            updateSectionTransform(id, { x: node.x(), y: node.y(), width, height, rotation: node.rotation() });
                        }}
                    />
                ))}

                {visibleSeats.map((seat) => (
                    <SeatingChartSeat
                        key={seat.id}
                        seat={seat}
                        isSelected={selectedIds.includes(seat.id)}
                        draggable={seatDraggable(seat)}
                        categoryColor={categoryColorFor(seat.categoryId)}
                        shapeRefs={shapeRefs}
                        onSelect={handleSelectShape}
                        onDoubleClick={handleSeatDoubleClick}
                        onDragEnd={(e, id) => updateSeatPosition(id, { x: e.target.x(), y: e.target.y() })}
                        onTransformEnd={(e, id) => {
                            const node = shapeRefs.current[id];
                            updateSeatTransform(id, { x: node.x(), y: node.y(), rotation: node.rotation() });
                            node.scale({ x: 1, y: 1 });
                        }}
                    />
                ))}

                {rowGroupLabels.map((rl) => (
                    <Group
                        key={`rowlabel-${rl.id}`}
                        x={rl.x}
                        y={rl.y}
                        rotation={rl.rotation}
                        listening={false}
                        ref={(node) => {
                            if (shapeRefs?.current) shapeRefs.current[`rowlabel-${rl.id}`] = node;
                        }}
                    >
                        <Text
                            text={rl.text}
                            width={ROW_LABEL_WIDTH}
                            height={ROW_LABEL_HEIGHT}
                            offsetX={ROW_LABEL_WIDTH / 2}
                            offsetY={ROW_LABEL_HEIGHT / 2}
                            rotation={-rl.rotation}
                            fontSize={12}
                            fill="white"
                            align="center"
                            verticalAlign="middle"
                            listening={false}
                        />
                    </Group>
                ))}

                {/* pre-click ghost + snap guide for seat/row/grid tools */}
                {showPreClickGhost && (
                    <>
                        <Circle x={ghostPosition.x} y={ghostPosition.y} radius={14} fill="#007AFF" opacity={0.4} listening={false} />
                        {hoverGuide.guideX != null && <Line points={[hoverGuide.guideX, 0, hoverGuide.guideX, 4000]} stroke="#00C853" dash={[2, 4]} listening={false} />}
                        {hoverGuide.guideY != null && <Line points={[0, hoverGuide.guideY, 4000, hoverGuide.guideY]} stroke="#00C853" dash={[2, 4]} listening={false} />}
                    </>
                )}

                {/* row tool: live preview seats + count + angle-snap guide */}
                {tool === SEAT_TOOLS.ROW && rowDraft && rowPreview && (
                    <>
                        <Line points={[rowDraft.start.x, rowDraft.start.y, rowDraft.current.x, rowDraft.current.y]} stroke="#007AFF" dash={[4, 4]} />
                        {rowPreview.seats.map((s, i) => (
                            <Circle key={i} x={s.x} y={s.y} radius={14} fill="#007AFF" opacity={0.5} listening={false} />
                        ))}
                        <Text x={rowDraft.current.x + 10} y={rowDraft.current.y - 20} text={`${rowPreview.descriptor.seatsPerRow} seats`} fill="white" fontSize={12} listening={false} />
                        {guideOrientation === "horizontal" && <Line points={[0, rowDraft.start.y, 4000, rowDraft.start.y]} stroke="#555" dash={[2, 4]} listening={false} />}
                        {guideOrientation === "vertical" && <Line points={[rowDraft.start.x, 0, rowDraft.start.x, 4000]} stroke="#555" dash={[2, 4]} listening={false} />}
                        {rowDraft?.guideX != null && <Line points={[rowDraft.guideX, 0, rowDraft.guideX, 4000]} stroke="#00C853" dash={[2, 4]} listening={false} />}
                        {rowDraft?.guideY != null && <Line points={[0, rowDraft.guideY, 4000, rowDraft.guideY]} stroke="#00C853" dash={[2, 4]} listening={false} />}
                    </>
                )}

                {/* grid tool: live preview seats + "R x C" label + snap guide */}
                {tool === SEAT_TOOLS.GRID && gridDraft && gridPreview && (
                    <>
                        <Rect
                            {...getNormalizedRect({
                                x: gridDraft.start.x,
                                y: gridDraft.start.y,
                                width: gridDraft.current.x - gridDraft.start.x,
                                height: gridDraft.current.y - gridDraft.start.y,
                            })}
                            stroke="#007AFF"
                            dash={[4, 4]}
                            fill="rgba(0,122,255,0.05)"
                        />
                        {gridPreview.seats.map((s, i) => (
                            <Circle key={i} x={s.x} y={s.y} radius={14} fill="#007AFF" opacity={0.5} listening={false} />
                        ))}
                        <Text x={gridDraft.current.x + 10} y={gridDraft.current.y - 20} text={`${gridPreview.rowCount} x ${gridPreview.seatsPerRow}`} fill="white" fontSize={14} fontStyle="bold" listening={false} />
                        {gridDraft?.guideX != null && <Line points={[gridDraft.guideX, 0, gridDraft.guideX, 4000]} stroke="#00C853" dash={[2, 4]} listening={false} />}
                        {gridDraft?.guideY != null && <Line points={[0, gridDraft.guideY, 4000, gridDraft.guideY]} stroke="#00C853" dash={[2, 4]} listening={false} />}
                    </>
                )}

                {tool === SEAT_TOOLS.SECTION && sectionDraft && (
                    <Rect {...getNormalizedRect(sectionDraft)} fill="rgba(0,122,255,0.05)" stroke="#007AFF" dash={[4, 4]} cornerRadius={6} />
                )}

                {tool === SEAT_TOOLS.DRAW && drawingRect && (
                    <Rect {...getNormalizedRect(drawingRect)} fill="#3a3a3a" stroke="#007AFF" dash={[4, 4]} cornerRadius={8} />
                )}

                {tool === SEAT_TOOLS.SELECT && marquee && (
                    <Rect {...getNormalizedRect(marquee)} fill="rgba(0,122,255,0.1)" stroke={theme.palette.primary.main} />
                )}

                {/* snap guide while dragging an existing multi-seat selection */}
                {groupDragGuide.guideX != null && <Line points={[groupDragGuide.guideX, 0, groupDragGuide.guideX, 4000]} stroke="#00C853" dash={[2, 4]} listening={false} />}
                {groupDragGuide.guideY != null && <Line points={[0, groupDragGuide.guideY, 4000, groupDragGuide.guideY]} stroke="#00C853" dash={[2, 4]} listening={false} />}

                {selectedIds.length > 0 && (
                    <Transformer
                        ref={transformerRef}
                        rotateEnabled={selectionKind !== "seats" || isCompleteRowSelection}
                        enabledAnchors={selectionKind === "seats" ? [] : undefined}
                        draggable={selectionKind === "seats" && isCompleteRowSelection}
                        shouldOverdrawWholeArea={selectionKind === "seats" && isCompleteRowSelection}
                        onDragMove={handleTransformerDragMove}
                        onDragEnd={handleTransformerDragEnd}
                        onDblClick={handleTransformerDblClick}
                        boundBoxFunc={(oldBox, newBox) => (newBox.width < 8 || newBox.height < 8 ? oldBox : newBox)}
                    />
                )}
            </Layer>
        </Stage>
    );
};

export default SeatingChartCanvasStage;
